import { useEffect, useState } from 'react';
import { realtimeClient, type ConnectionState } from '../../api/realtime';
import { getReachability, onReachabilityChange, type ReachabilityState } from '../../api/client';
import { deriveConnectionStatus } from './connectionStatus';
import { VisuallyHidden } from '../../ui';
import { useT } from '../../i18n/i18n.tsx';

/**
 * DESIGN.md §6.4: the ONE instance of this component, living in the status bar. "Silence is the
 * reward for working" — online (and REST-reachable) shows nothing but the dot. Every other state
 * names itself in words next to the dot, never colour alone (§2.4).
 */
export function ConnectionIndicator() {
  const t = useT();
  const [realtimeState, setRealtimeState] = useState<ConnectionState>(realtimeClient.getState());
  const [since, setSince] = useState(() => Date.now());
  const [reachability, setReachability] = useState<ReachabilityState>(getReachability());
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));

  useEffect(() => {
    const unsubscribe = realtimeClient.onStateChange((next) => {
      setRealtimeState(next);
      setSince(Date.now());
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    return onReachabilityChange(setReachability);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onOnline() {
      setOnline(true);
    }
    function onOffline() {
      setOnline(false);
    }
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const [, forceTick] = useState(0);
  useEffect(() => {
    if (realtimeState === 'online' || realtimeState === 'connecting') return;
    const id = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [realtimeState]);

  const status = deriveConnectionStatus({
    realtimeState,
    reachability,
    online,
    targetHost: realtimeClient.getTargetHost(),
    sinceMs: since,
    nowMs: Date.now(),
  });

  // MVP-272: `deriveConnectionStatus` hands over keys, not words — DESIGN.md §6.4's vocabulary is
  // shared with the guest page and `App.tsx`, so it cannot be written in any one language upstream.
  // The ` · ` between label and detail belongs here too: a detail-less status must not render a
  // dangling separator, so the join is the renderer's job rather than the pure module's.
  const label = status.labelKey ? t(status.labelKey) : null;
  const detail = status.detail ? t(status.detail.key, status.detail.params) : null;

  return (
    <div className="connection-indicator" title={label ?? t('common.connection.connected')} data-testid="connection-indicator">
      <span className={`connection-indicator__dot connection-indicator__dot--${status.tone}`} aria-hidden="true" />
      {label && (
        <span className="connection-indicator__label text-meta tabular-nums">
          {detail ? `${label} · ${detail}` : label}
        </span>
      )}
      <VisuallyHidden>
        {t('common.connection.srStatus', { status: label ?? t('common.connection.connected') })}
      </VisuallyHidden>
    </div>
  );
}
